import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"

import { useCreatePurchasingCost } from "../../../hooks/useCreatePurchasingCost"
import { useUpdatePurchasingCost } from "../../../hooks/useUpdatePurchasingCost"

import {
  PURCHASE_COST_DEFAULT_FORM_VALUES,
  type PurchaseCostFormValues,
  purchaseCostFormSchema,
} from "./purchaseCostFormSchema"
import { PurchaseCostFormStep } from "./purchaseCostFormStep"

import type {
  AddPurchasingCostInput,
  EditPurchasingCostInput,
  PurchasingCostListEntry,
} from "@/graphql/codegen/graphql"

interface UsePurchaseCostFormParams {
  productId: number
  defaultWarehouseAreaId: string | null
  editingCost: PurchasingCostListEntry | null
  onClose: () => void
  onSuccess?: () => void
}

interface UsePurchaseCostFormReturn {
  methods: UseFormReturn<PurchaseCostFormValues>
  step: PurchaseCostFormStep
  handleStep1Continue: () => Promise<void>
  handleBack: () => void
  handleSubmit: () => Promise<void>
  canSubmit: boolean
  isEdit: boolean
}

const toPurchaseCostFormValues = (
  defaultWarehouseAreaId: string | null,
  editingCost: PurchasingCostListEntry | null
): PurchaseCostFormValues => {
  if (!editingCost) {
    return {
      ...PURCHASE_COST_DEFAULT_FORM_VALUES,
      warehouseAreaId: defaultWarehouseAreaId ?? "",
    }
  }

  return {
    warehouseAreaId: editingCost.warehouseAreaId,
    tiers: editingCost.tiers.map((tier) => ({ uomId: tier.uomId, qty: tier.qty, price: tier.price })),
    effectiveDate: editingCost.effectiveDate,
    expirationDate: editingCost.expirationDate ?? null,
    freightTerms: editingCost.freightTerms ?? "",
    incoterms: (editingCost.incoterms ?? "") as PurchaseCostFormValues["incoterms"],
    incotermsLocationId: editingCost.incotermsLocationId ?? editingCost.incotermsLocation?.locationId ?? "",
  }
}

const toCreatePurchasingCostInput = (
  productId: number,
  formValues: PurchaseCostFormValues
): AddPurchasingCostInput => ({
  productId,
  shipToId: formValues.warehouseAreaId,
  uomId: formValues.tiers[0]?.uomId ?? "",
  tiers: formValues.tiers.map((tier) => ({
    uomId: tier.uomId,
    qty: tier.qty ?? 0,
    price: tier.price ?? 0,
  })),
  effectiveDate: formValues.effectiveDate,
  expirationDate: formValues.expirationDate ?? undefined,
  freightTerms: formValues.freightTerms || undefined,
  incoterms: formValues.incoterms,
  incotermsLocationId: formValues.incotermsLocationId,
})

const toUpdatePurchasingCostInput = (id: string, formValues: PurchaseCostFormValues): EditPurchasingCostInput => ({
  id,
  uomId: formValues.tiers[0]?.uomId ?? "",
  tiers: formValues.tiers.map((tier) => ({
    uomId: tier.uomId,
    qty: tier.qty ?? 0,
    price: tier.price ?? 0,
  })),
  effectiveDate: formValues.effectiveDate,
  expirationDate: formValues.expirationDate ?? undefined,
  freightTerms: formValues.freightTerms || undefined,
  incoterms: formValues.incoterms,
  incotermsLocationId: formValues.incotermsLocationId,
})

export const usePurchaseCostForm = ({
  productId,
  defaultWarehouseAreaId,
  editingCost,
  onClose,
  onSuccess,
}: UsePurchaseCostFormParams): UsePurchaseCostFormReturn => {
  const methods = useForm<PurchaseCostFormValues>({
    defaultValues: toPurchaseCostFormValues(defaultWarehouseAreaId, editingCost),
    resolver: zodResolver(purchaseCostFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isValid, isSubmitting },
    trigger,
  } = methods

  const [step, setStep] = useState<PurchaseCostFormStep>(PurchaseCostFormStep.Details)
  const { createPurchasingCost, isCreating } = useCreatePurchasingCost()
  const { updatePurchasingCost, isUpdating } = useUpdatePurchasingCost()

  const handleStep1Continue = async () => {
    const isStep1Valid = await trigger(["warehouseAreaId", "tiers", "effectiveDate", "expirationDate"])
    if (isStep1Valid) setStep(PurchaseCostFormStep.ShippingTerms)
  }

  const handleBack = () => setStep(PurchaseCostFormStep.Details)

  const handleSubmit = methods.handleSubmit(async (formValues) => {
    const isSuccess = editingCost
      ? await updatePurchasingCost(toUpdatePurchasingCostInput(editingCost.id, formValues))
      : await createPurchasingCost(toCreatePurchasingCostInput(productId, formValues))

    if (isSuccess) {
      onSuccess?.()
      onClose()
    }
  })

  const canSubmit = isValid && !isSubmitting && !isCreating && !isUpdating

  return {
    methods,
    step,
    handleStep1Continue,
    handleBack,
    handleSubmit,
    canSubmit,
    isEdit: Boolean(editingCost),
  }
}
